/** English translations. */
export const en = {
  app: {
    name: 'uSolar',
    tagline: 'Monitoring and management of photovoltaic plants',
    loading: 'Loading…',
    retry: 'Retry',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    logout: 'Sign out',
    language: 'Language',
    tenant: 'Customer',
    noData: 'No data available',
    unknown: 'unknown',
    table: 'Table',
    chart: 'Chart',
  },
  nav: {
    portfolio: 'Portfolio',
    plants: 'Plants',
    compare: 'Compare',
    incidents: 'Incidents',
    admin: 'Connectors',
    reports: 'Reports',
    mis: 'Management',
  },
  login: {
    title: 'Sign in',
    email: 'E-mail',
    password: 'Password',
    submit: 'Sign in',
    mfaTitle: 'Second factor',
    mfaHint: 'Enter the six-digit code from your app (or a recovery code).',
    mfaCode: 'Code',
    mfaSubmit: 'Confirm',
    enrollTitle: 'Set up two-factor authentication',
    enrollHint:
      'Your role requires two-factor authentication. Scan the QR code with an authenticator app, or type the secret in by hand, then confirm with the generated code.',
    enrollQr: 'Scan this QR code with your authenticator app',
    enrollQrAlt: 'QR code holding the otpauth link, which is also written out as text below',
    enrollSecret: 'Secret',
    enrollUri: 'otpauth link',
    recoveryTitle: 'Recovery codes',
    recoveryHint: 'Store these somewhere safe. Each works once and will not be shown again.',
    recoveryDone: 'Saved, continue',
    selectTenant: 'Select customer',
  },
  kpi: {
    currentPower: 'Current power',
    energyToday: 'Energy today',
    specificYield: 'Specific yield',
    dcCapacity: 'Installed DC capacity',
    irradiance: 'Irradiance',
    temperature: 'Module temperature',
    devices: 'Devices',
    openIncidents: 'Open incidents',
    plants: 'Plants',
    reporting: 'reporting fresh data',
    coverage: 'Data coverage',
    energyMethod: {
      counter: 'from inverter counter',
      integral: 'integrated from power',
      mixed: 'mixed',
    },
  },
  freshness: {
    online: 'live',
    stale: 'stale',
    offline: 'offline',
    unknown: 'no data',
    error: 'device error',
    ageSeconds: '{{value}} s old',
    ageMinutes: '{{value}} min old',
    ageHours: '{{value}} h old',
    never: 'never reported',
    tooltip: 'Source time: {{sampleTime}} · expected cycle {{cycle}} s',
  },
  plant: {
    title: 'Plant',
    devices: 'Devices',
    production: 'Production and power',
    incidents: 'Incidents',
    report: 'Daily report',
    noIrradiance: 'This plant has no irradiance sensor — irradiance is not measured.',
    capacityUnknown: 'Installed capacity is unknown, specific yield cannot be computed.',
    partialPower: 'The power sum excludes devices without fresh data.',
  },
  device: {
    name: 'Device',
    kind: 'Type',
    status: 'Status',
    power: 'Power',
    energyTotal: 'Lifetime energy',
    sampleTime: 'Sample time',
    sourceTime: 'Source time',
    ingestedAt: 'Stored at',
    quality: 'Quality',
    serial: 'Serial number',
    capacity: 'DC capacity',
    section: 'Circuit',
    errorCode: 'Error code',
    statusCode: 'Operating state',
    kinds: {
      inverter: 'inverter',
      irradiance_sensor: 'irradiance sensor',
      temperature_sensor: 'temperature sensor',
      meter: 'meter',
      logger: 'data logger',
      breaker: 'breaker',
      other: 'other',
    },
    qualities: {
      good: 'good',
      stale: 'stale',
      estimated: 'estimated',
      suspect: 'suspect',
      error: 'error',
    },
    noSourceTime: 'not provided by source',
  },
  chart: {
    power: 'Power (kW)',
    energy: 'Energy (kWh)',
    irradiance: 'Irradiance (W/m²)',
    normalized: 'Normalised per kWp',
    resolution: 'Resolution',
    range: 'Period',
    exportCsv: 'Export CSV',
    ranges: {
      today: 'Today',
      yesterday: 'Yesterday',
      week: '7 days',
      month: '30 days',
      year: 'Year',
    },
    resolutions: {
      raw: 'raw',
      '5min': '5 min',
      '15min': '15 min',
      hour: 'hour',
      day: 'day',
      month: 'month',
    },
  },
  compare: {
    title: 'Compare devices and circuits',
    hint: 'Pick devices to compare in the same time window.',
    normalize: 'Normalise per installed capacity (kWp)',
    capacityUnknown: 'Installed capacity is unknown for some devices — absolute values shown.',
    selectDevices: 'Devices',
    run: 'Compare',
  },
  incidents: {
    title: 'Incidents',
    openOnly: 'Open only',
    severity: 'Severity',
    opened: 'Opened',
    device: 'Device',
    status: 'Status',
    evidence: 'Evidence',
    acknowledge: 'Acknowledge',
    closeIncident: 'Close',
    resolution: 'Resolution',
    rootCause: 'Root cause',
    none: 'No incidents',
    severities: { info: 'info', warning: 'warning', major: 'major', critical: 'critical' },
    statuses: {
      new: 'new',
      assigned: 'assigned',
      in_progress: 'in progress',
      waiting: 'waiting',
      closed: 'closed',
    },
    resolutions: {
      fixed: 'fixed',
      false_alarm: 'false alarm',
      curtailment: 'curtailment',
      no_fault_found: 'no fault found',
      deferred: 'deferred',
      other: 'other',
      auto_cleared: 'auto cleared',
    },
    rules: {
      'comm.source_unreachable': 'Logger {{source}} is not answering',
      'device.offline': 'Device {{device}} reports no data',
      'device.error_code': 'Device {{device}} reports error {{code}}',
      'device.zero_production': 'Device {{device}} produces nothing while its peers do',
    },
    suspicionNotice:
      'This is a suspicion derived from data, not a proven defect. Verify on site before replacing components.',
  },
  admin: {
    title: 'Data sources (connectors)',
    readOnlyNotice:
      'All connectors are read-only. The system cannot write to or switch plant technology.',
    endpoint: 'Endpoint',
    connector: 'Connector',
    interval: 'Poll interval',
    lastSuccess: 'Last success',
    failures: 'Consecutive failures',
    probe: 'Check reachability',
    poll: 'Poll now',
    runs: 'Ingest history',
    probeOk: 'Logger answered ({{latency}} ms)',
    probeFail: 'Logger did not answer: {{detail}}',
    pollDone: 'Stored {{samples}} values from {{devices}} devices',
  },
  report: {
    title: 'Daily report',
    date: 'Date',
    energy: 'Energy',
    coverage: 'Coverage',
    method: 'Method',
    downloadCsv: 'Download CSV',
    disclaimers: {
      partial_coverage: 'The report does not cover the whole period — data is missing.',
      coverage_unknown: 'Data coverage could not be determined.',
      dc_capacity_unknown: 'Installed capacity is unknown, specific yield is missing.',
      energy_partly_integrated_from_power:
        'Part of the energy was integrated from power instead of read from the inverter counter.',
    },
  },
  mis: {
    periods: {
      this_month: 'This month',
      last_month: 'Last month',
      this_quarter: 'This quarter',
      this_year: 'This year',
      last_12_months: '12 months',
      custom: 'Custom',
    },
    energy: 'Energy',
    energyKwh: 'Energy (kWh)',
    plantCount_one: '{{count}} plant',
    plantCount_other: '{{count}} plants',
    peakPower: 'Peak power',
    dataAvailability: 'Data availability',
    availabilityHint: 'Share of expected measurements actually received.',
    performanceRatio: 'Performance ratio',
    incidentsOpened: 'Incidents opened',
    closed: 'closed',
    mtta: 'MTTA (time to acknowledge)',
    mttr: 'MTTR (time to resolve)',
    trend: 'Production trend',
    monthlyEnergy: 'Monthly energy',
    month: 'Month',
    ranking: 'Plant comparison',
    rankingExcluded_one: '{{count}} plant stays out of the comparison for lack of installed capacity.',
    rankingExcluded_other: '{{count}} plants stay out of the comparison for lack of installed capacity.',
    capacityMissing_one: '{{count}} plant without installed capacity',
    capacityMissing_other: '{{count}} plants without installed capacity',
    service: 'Service KPIs',
    byRule: 'By rule',
    rule: 'Rule',
    noData: 'no data',
    notAvailable: 'cannot be computed',
    minutes: '{{value}} min',
    hours: '{{value}} h',
    days: '{{value}} days',
    generatedAt: 'Generated {{value}}',
    ruleNames: {
      'comm.source_unreachable': 'Logger unreachable',
      'device.offline': 'Device reports no data',
      'device.error_code': 'Device error code',
      'device.zero_production': 'Zero production vs peers',
    },
    notes: {
      availability_is_data_based:
        'Availability here means data availability, not technical availability of the plant — that needs the expected-yield model of phase 3.',
      no_irradiance_sensor:
        'None of the plants has an irradiance sensor, so no performance ratio is computed.',
      irradiance_coverage_low:
        'The irradiance sensor delivered too little data for the period, so the performance ratio is not reported.',
      irradiance_partial: 'Irradiance measurement is incomplete for the period.',
      partial_coverage: 'Part of the period has no data — the figures are partial.',
      dc_capacity_unknown:
        'Some plants have no installed capacity on record; specific yield and PR are missing for them.',
      energy_partly_integrated_from_power:
        'Part of the energy is integrated from power instead of read from the inverter counter.',
      devices_without_data: 'Some devices reported no data during the period.',
      months_without_data: 'Months without data are left empty — they do not mean zero production.',
    },
  },
  errors: {
    'auth.unauthorized': 'You are not signed in.',
    'auth.invalid_credentials': 'Invalid e-mail or password.',
    'auth.account_locked': 'The account is temporarily locked after repeated attempts.',
    'auth.account_disabled': 'The account is disabled.',
    'auth.rate_limited': 'Too many attempts. Try again shortly.',
    'auth.mfa_required': 'Two-factor authentication must be completed.',
    'auth.mfa_invalid_code': 'Invalid code.',
    'auth.mfa_not_started': 'Second-factor setup has not been started.',
    'auth.mfa_already_enabled': 'Two-factor authentication is already enabled.',
    'auth.forbidden': 'You are not allowed to do that.',
    'auth.permission_denied': 'You are not allowed to do that ({{permission}}).',
    'auth.tenant_not_selected': 'Select a customer.',
    'auth.tenant_not_allowed': 'You have no access to this customer.',
    'auth.csrf_failed': 'The session expired. Reload the page and try again.',
    'resource.not_found': 'Not found.',
    'plant.not_found': 'Plant not found.',
    'device.not_found': 'Device not found.',
    'incident.not_found': 'Incident not found.',
    'incident.already_closed': 'The incident is already closed.',
    'telemetry.invalid_range': 'Invalid time range.',
    'telemetry.range_too_large': 'Range too long (max {{max_days}} days at this resolution).',
    'telemetry.unknown_metric': 'Unknown metric.',
    'report.future_date': 'A report cannot be generated for a future date.',
    'request.invalid': 'Invalid request.',
    'server.error': 'Server error. Please try again.',
    generic: 'Something went wrong ({{code}}).',
  },
};
